import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  api,
  apiError,
  API_ORIGIN,
  FTP_PREVIEW_MODE,
  clearStoredTokens,
  setStoredTokens,
} from "@/lib/api";

// On a static FTP client preview there is no API host, so the admin session probe would
// only produce expected 404 noise on public pages.
const NO_API = FTP_PREVIEW_MODE && !API_ORIGIN;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    if (NO_API) {
      setUser(false);
      return false;
    }
    try {
      const { data } = await api.get("/admin/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/admin/auth/login", { email, password });
      setStoredTokens(data || {});
      setUser(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: apiError(err, "Unable to sign in.") };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/admin/auth/logout");
    } catch {
      /* the session is cleared client-side regardless */
    }
    clearStoredTokens();
    setUser(false);
  }, []);

  const can = useCallback(
    (capability) => {
      if (!user) return false;
      const map = {
        "content.write": ["super_admin", "administrator", "editor"],
        "media.write": ["super_admin", "administrator", "editor"],
        moderate: ["super_admin", "administrator", "editor", "reviewer"],
        "emerald.content": ["super_admin", "administrator", "editor"],
        "settings.write": ["super_admin", "administrator"],
        "users.manage": ["super_admin", "administrator"],
        "audit.read": ["super_admin", "administrator"],
      };
      return (map[capability] || []).includes(user.role);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, refresh, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  administrator: "Administrator",
  editor: "Editor",
  reviewer: "Reviewer",
};
